import { useMemo } from "react";
import { useLang } from "@/context/LanguageContext";
import { useSiteSettings } from "@/context/LanguageContext";
import { islandUrl, propertyUrl } from "@/lib/translations";

/** Eingebaute Textvariablen – {schlüssel} in Texten (Startseite, FAQ, Insel-/Orts-/Objektseiten, Seiten, SEO) wird ersetzt. */
export const BUILTIN_VARS = [
  { key: "firma", label: "Firmenname", scope: "überall", example: "privateHOLIDAY S.L." },
  { key: "telefon", label: "Telefon (Einstellungen → Firma)", scope: "überall" },
  { key: "email", label: "E-Mail (Einstellungen → Firma)", scope: "überall" },
  { key: "site_url", label: "Website-Adresse", scope: "überall", example: "https://www.privateholiday.de" },
  { key: "jahr", label: "Aktuelles Jahr", scope: "überall", example: String(new Date().getFullYear()) },
  { key: "insel", label: "Inselname (in der Sprache des Besuchers)", scope: "Insel-, Orts- und Objektseiten", example: "Teneriffa" },
  { key: "insel_url", label: "Pfad der Inselseite", scope: "Insel-, Orts- und Objektseiten", example: "/teneriffa" },
  { key: "ort", label: "Ortsname", scope: "Orts- und Objektseiten", example: "Puerto de la Cruz" },
  { key: "ort_url", label: "Pfad der Ortsseite", scope: "Orts- und Objektseiten" },
  { key: "objektnummer", label: "Objekt-Nr.", scope: "Objektseiten", example: "8272" },
  { key: "objekttitel", label: "Objekttitel", scope: "Objektseiten" },
  { key: "objekt_url", label: "Pfad der Objektseite", scope: "Objektseiten" },
  { key: "personen", label: "Max. Gäste", scope: "Objektseiten", example: "4" },
];

const VAR_RE = /\{([a-zA-Z0-9_]+)\}/g;

/** Ersetzt {schlüssel}; unbekannte Variablen bleiben unverändert stehen. */
export const applyVars = (text, vars) => {
  if (!text || typeof text !== "string" || !text.includes("{")) return text;
  return text.replace(VAR_RE, (m, k) => { const v = vars?.[k.toLowerCase()]; return v === undefined || v === null ? m : String(v); });
};

const langVal = (v, lang) => (v && typeof v === "object" ? (v[lang] || v.de || Object.values(v).find(Boolean) || "") : (v ?? ""));

/** Variablen aus Einstellungen (Firma + eigene Variablen) und Seitenkontext (Objekt / Insel / Ort) zusammenstellen. */
export const buildVars = ({ site = {}, lang = "de", t, prop, island, place }) => {
  const vars = {
    firma: site.company_name || "privateHOLIDAY", telefon: site.company_phone || "", email: site.company_email || "",
    site_url: (site.site_url || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, ""), jahr: String(new Date().getFullYear()),
  };
  const isl = island || prop?.island || place?.island;
  if (isl) { vars.insel = t?.islands?.[isl] || isl; vars.insel_url = islandUrl(isl, lang); }
  const ortName = place?.name || prop?.location;
  if (ortName) { vars.ort = ortName; vars.ort_url = place?.path || (isl ? `${islandUrl(isl, lang)}/${(place?.slug || ortName).toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}` : ""); }
  if (prop) {
    vars.objektnummer = prop.ref_number != null ? String(prop.ref_number) : "";
    vars.objekttitel = langVal(prop.h1 || prop.title, lang);
    vars.objekt_url = propertyUrl(prop, lang);
    vars.personen = prop.max_guests != null ? String(prop.max_guests) : "";
  }
  (site.text_variables || []).forEach((v) => { if (v?.key) vars[String(v.key).toLowerCase()] = langVal(v.values, lang); });
  return vars;
};

/** Hook: Variablen für die aktuelle Seite (optionaler Kontext prop/island/place). */
export const useTextVars = (ctx = {}) => {
  const site = useSiteSettings();
  const { lang, t } = useLang();
  const { prop, island, place } = ctx;
  return useMemo(() => buildVars({ site, lang, t, prop, island, place }), [site, lang, t, prop, island, place]);
};
